// packages/web-ui/src/state/inline-cards.ts
//
// Story W2-S6 — `inline-cards` zustand slice. Owns the per-card UX state
// for the inline cards rendered inside chat-message bubbles
// (ResponseViewProposal, FileUploadConfirm, Clarification, KbChangeTreeShell).
//
// SCOPE BOUNDARY: this slice ONLY tracks card-side ephemeral state — card
// terminal status, selected pill index, last error, "submitted" replies. It
// does NOT mutate the `chat` slice (no message reducer touches), does NOT
// post network calls, and does NOT subscribe to SSE. The integration tick
// (W2-S6-integration) wires:
//   1. SSE event → `attachCardToMessage` on the `chat` slice
//   2. <MessageBubble> → reads `chat.messagesById[*].inlineCards[]` and
//      renders each via `<InlineCardSlot>`
//   3. card click handlers (Submit / Open / Confirm) — they call
//      `viewsApi`/`uploadsApi`/`chat.sendMessage` AND patch this slice
//      via the actions defined here.
//
// Why a dedicated slice instead of extending `chat.ts`?
//  - The orchestrator brief explicitly fences this story away from
//    `panels/chat-panel/*` and `state/chat.ts` — those are owned by
//    sibling agents. A standalone slice for card UX state lets W2-S6
//    land without contention; the integration agent merges the
//    consumer-side wiring on the next tick.
//  - Idempotency / replay-safe append is encoded here (the SSE replay
//    pitfall from AC #8) so the integration agent inherits it for free
//    when it re-publishes through chat.attachCardToMessage.
//
// Identity-key contract — see `cardIdentityKey()` for canonical impl. The
// SSE event handler MUST hash on this key when attaching a card to a
// message; replays of the same event MUST be no-ops.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';

// ─── Discriminated-union envelope (mirrors AC #1) ────────────────────────
//
// IMPORTANT: envelope props are DATA-ONLY (serialisable). Click handlers
// (Keep & Open, Discard, Submit, etc.) are provided by `<InlineCardSlot>`
// at render time — the integration agent (W2-S6-integration) composes
// handlers from the `views` slice / `chat.sendMessage` / `useOpenResource`
// hook and threads them through the slot. This keeps the slice
// snapshottable (e.g. for session resume) and avoids stale closures.

/**
 * Data props for the response-view envelope. The server writes every view
 * to its permanent path before emitting `kb:viewRendered`, so there is no
 * intermediate proposal / discarded state to carry on the wire.
 */
export interface ResponseViewProposalEnvelopeData {
  viewId: string;
  viewName: string;
  elementCount?: number;
  sourceQuestion?: string;
  /**
   * LV3-S1 — server-computed eligibility flag (the `rasterOffered` return of
   * `NodeAutoViewWriter.run`). When true the view is eligible for the opt-in
   * "Generate LLM visualization" follow-up. Observability-only on the server
   * (no `image:*` event is auto-fired); the card consumes it to GATE the
   * button. Optional ⇒ absent for legacy/deterministic-only views (defaults
   * falsy = no button, byte-identical to the switch-OFF path).
   */
  rasterOffered?: boolean;
  /**
   * LV3-S1 — mirror of the effective `views.llmImage.enabled` setting, read
   * once at card-compose time in ChatPanel. The "Generate LLM visualization"
   * button is gated on `rasterOffered === true && llmImageEnabled === true`
   * (belt-and-suspenders: the server already encodes the enable flag into
   * `rasterOffered`, so this is a redundant UI guard). Optional, defaults
   * falsy ⇒ switch-OFF renders no button.
   */
  llmImageEnabled?: boolean;
  /**
   * LV-relayout fix — monotonically-bumped revision counter that forces the
   * inline preview to re-fetch the latest `layout.json` when the server
   * re-runs the layout (a chat "adjust layout" request → `relayoutView` →
   * a re-emitted `kb:viewRendered` for the SAME viewId). `attachCard` is a
   * replay-safe no-op on an already-mounted `(messageId, viewId)`, so a
   * re-emit would otherwise freeze the card's envelope props from the FIRST
   * emit and the inline sketch would never re-render the new arrangement.
   * The SSE handler bumps this on every re-emit for an existing card; the
   * card re-keys its one-shot auto-fetch guard on it. Absent / 0 on the first
   * emit ⇒ the new-view mount + single fetch path is byte-identical to before.
   */
  layoutRevision?: number;
  /**
   * LV3-S4 — the LV2-S3 escalation reason recorded when THIS view auto-escalated
   * its sticky lead to the `llm-image` raster (an expressiveness gap the precise
   * renderer couldn't honour, or a detected frustration signal). The server has
   * no manifest field nor `image:*` payload carrying the reason — it announces it
   * ONCE via a broadcast `system:notice` whose message is
   * `engine-escalation:<reason>:<viewId>`. The `attach-to-chat-slice` dispatcher
   * parses that control notice and patches it onto the matching card's envelope
   * here, so the ORIGINATING card can render the LV3-S4 rationale + "Back to
   * sketch" control (gated additionally on the raster being ready / loading). The
   * "Back to sketch" reset clears this field locally (durable un-escalation) so
   * the rationale does not reappear on the next re-render. Absent ⇒ the view was
   * NOT auto-escalated (manual generation or a plain sketch) ⇒ no escalation
   * subtree (byte-identical to the non-escalated / switch-OFF path).
   */
  escalated?: 'expressiveness-gap' | 'frustration';
}

/** Data props for the file-upload-confirm envelope. Mirrors AC #3. */
export interface FileUploadConfirmEnvelopeData {
  /**
   * Stable identity — the integration agent generates this at composer
   * drop time (e.g. UUID + name + size). The slice uses it for
   * idempotent attach. The actual `File` object lives in the composer's
   * staging map (NOT in the slice — `File` is non-serialisable).
   */
  uploadHandle: string;
  fileName: string;
  fileSize: number;
  contentType?: string;
}

/** Data props for the clarification envelope. Mirrors AC #4. */
export interface ClarificationEnvelopeData {
  question: string;
  options?: string[];
}

/** Data props for the KB change-tree shell envelope. Mirrors AC #5. */
export interface KbChangeTreeShellEnvelopeData {
  changesetId: string;
  entityCount: number;
}

/**
 * Data props for the changeset preview card — visual diff overlay that
 * sits ABOVE the `<KbChangeTreeShell>` for the same changeset. Carries
 * the changesetId so the slot can look up the pre-laid-out
 * `ReactFlowLayoutJson` payload from the dedicated preview store / SSE
 * cache. Per Story W2-S6 contract: envelope props are data-only +
 * serialisable.
 */
export interface ChangesetPreviewEnvelopeData {
  changesetId: string;
}

/**
 * IV3-S1 / IV3-S2 — data props for the LLM-raster ("illustrative-image")
 * card. Mounted on `image:genStarted` (skeleton) and resolved on
 * `image:genReady` purely by `imageViewId` — the live status/imageUrl live
 * in the `image-views` slice (keyed by `imageViewId`), NOT here, so the card
 * survives re-resolution-free updates (the wrong-bubble fix). Envelope props
 * stay data-only + serialisable per the W2-S6 contract.
 */
export interface IllustrativeImageEnvelopeData {
  /** Stable correlation id tying genStarted ⇆ genReady ⇆ image-views record. */
  imageViewId: string;
  /** The view this card renders (PNG/SysML source path + NL-edit POST target). */
  viewId: string;
  /** Workspace-relative path to the authoritative `view.sysml` (pillar). */
  sysmlSourcePath?: string;
  /** Alt text derived from the SceneDescription (a11y). */
  sceneDescriptionAlt?: string;
}

/**
 * One grouped neighbour row of an {@link EntityConnectionsEnvelopeData}. Mirrors
 * the server's `KbEntityConnectionsRow` (data-only, serialisable).
 */
export interface EntityConnectionsRow {
  neighbour: string;
  entityId: string;
  direction: 'incoming' | 'outgoing';
  flowTypes: string[];
  ports: Array<{ targetPort: string; neighbourPort: string }>;
  level?: string;
}

/**
 * Data props for the deterministic connections-table card. The server derives
 * the COMPLETE neighbour set from the KB (`buildEntityAdjacency`) and emits it
 * on `kb:entityConnections`; the card renders it verbatim so completeness never
 * depends on the LLM transcribing rows into markdown. Data-only + serialisable
 * per the W2-S6 contract.
 */
export interface EntityConnectionsEnvelopeData {
  /** Simple name of the queried entity. */
  target: string;
  /** Fully-qualified name — the stable identity key. */
  targetQualifiedName: string;
  /** Complete grouped neighbour rows (incoming + outgoing). */
  rows: EntityConnectionsRow[];
  /** Raw connection edges before grouping — for the "N connections" caption. */
  edgeCount: number;
}

/**
 * Data props for the generic deterministic data-table card. The server derives
 * the COMPLETE `{columns, rows}` table from the KB (`deriveDataTable`) and emits
 * it on `kb:dataTable`; the card renders it verbatim so completeness never
 * depends on the LLM transcribing rows. Shared by every tabular skill
 * (hierarchy / weight / budget / bom / trace / safety / governance). Data-only
 * + serialisable per the W2-S6 contract.
 */
export interface DataTableEnvelopeData {
  /** Stable identity, e.g. `"hierarchy:Pkg::Part"`. */
  cardId: string;
  /** Card title. */
  title: string;
  /** Optional one-line caption. */
  caption?: string;
  /** Header labels. */
  columns: string[];
  /** One row per item; each row's cells align to `columns`. */
  rows: string[][];
}

/** Discriminated-union envelope. Re-exported via `components/inline-cards`. */
export type InlineCardEnvelope =
  | { kind: 'response-view-proposal'; props: ResponseViewProposalEnvelopeData }
  | { kind: 'file-upload-confirm'; props: FileUploadConfirmEnvelopeData }
  | { kind: 'clarification'; props: ClarificationEnvelopeData }
  | { kind: 'kb-change-tree-shell'; props: KbChangeTreeShellEnvelopeData }
  | { kind: 'changeset-preview'; props: ChangesetPreviewEnvelopeData }
  | { kind: 'illustrative-image'; props: IllustrativeImageEnvelopeData }
  | { kind: 'entity-connections'; props: EntityConnectionsEnvelopeData }
  | { kind: 'data-table'; props: DataTableEnvelopeData };

/**
 * Stable identity key for replay-safe attachment. The key encodes the
 * primary id field of each envelope kind:
 *
 *  - response-view-proposal       → viewId
 *  - file-upload-confirm          → file.name + file.size + file.lastModified
 *  - clarification                → question (the orchestrator does not
 *                                    yet emit a clarificationId; the
 *                                    question text is treated as the key)
 *  - kb-change-tree-shell         → changesetId
 *
 * Two envelopes attached to the same message with the same identity key
 * MUST be coalesced (AC #8 SSE replay safety).
 */
export function cardIdentityKey(envelope: InlineCardEnvelope): string {
  switch (envelope.kind) {
    case 'response-view-proposal':
      return `response-view-proposal:${envelope.props.viewId}`;
    case 'file-upload-confirm':
      return `file-upload-confirm:${envelope.props.uploadHandle}`;
    case 'clarification':
      return `clarification:${envelope.props.question}`;
    case 'kb-change-tree-shell':
      return `kb-change-tree-shell:${envelope.props.changesetId}`;
    case 'changeset-preview':
      return `changeset-preview:${envelope.props.changesetId}`;
    case 'illustrative-image':
      // Key on the stable correlation id so `image:genReady` never re-appends
      // a second card — the genStarted envelope is updated in place via the
      // image-views slice (the wrong-bubble fix is idempotent here too).
      return `illustrative-image:${envelope.props.imageViewId}`;
    case 'entity-connections':
      // Key on the target's qualifiedName so a re-emit for the same entity in
      // the same turn coalesces (replay-safe, one card per target).
      return `entity-connections:${envelope.props.targetQualifiedName}`;
    case 'data-table':
      // The server-supplied cardId already encodes kind + target, so it is the
      // stable coalescing key (replay-safe, one card per kind+target).
      return `data-table:${envelope.props.cardId}`;
  }
}

// ─── Card UX state ───────────────────────────────────────────────────────

/** Per-card ephemeral UX state. Keyed by `(messageId, identityKey)`. */
export interface InlineCardUxState {
  /** Last user-facing error for the card (Upload). */
  lastError?: string;
  /** Clarification submitted reply ("you answered: …" persistence). */
  submittedReply?: string;
  /** Whether the card is in a `pending` (network call in flight) phase. */
  pending?: boolean;
}

export interface InlineCardsSliceState {
  /**
   * Idempotent envelope log — `cardsByMessageId[messageId]` is the ordered
   * list of envelopes attached to that assistant message. Append-only
   * (deletion is not a supported operation).
   */
  cardsByMessageId: Record<string, InlineCardEnvelope[]>;
  /**
   * Per-card UX state. Keyed by `${messageId}:${cardIdentityKey(envelope)}`.
   * The compound key preserves cards across SSE replays where the same
   * envelope re-arrives — the second arrival is a no-op append (idempotent)
   * AND the existing UX state is retained.
   */
  cardUxByKey: Record<string, InlineCardUxState>;
}

export const INLINE_CARDS_INITIAL_STATE: InlineCardsSliceState = {
  cardsByMessageId: {},
  cardUxByKey: {},
};

export interface InlineCardsSliceActions {
  /**
   * Idempotent attach. If the same `(messageId, identityKey)` already
   * exists, this is a no-op. Returns the canonical card index in
   * `cardsByMessageId[messageId]` (existing or newly-appended).
   */
  attachCard: (messageId: string, envelope: InlineCardEnvelope) => number;
  /**
   * Patch a card's UX state. Caller passes the `messageId` + the envelope
   * (so we can compute the identity key). The patch is shallow-merged onto
   * the current UX state.
   */
  patchCardUx: (
    messageId: string,
    envelope: InlineCardEnvelope,
    patch: Partial<InlineCardUxState>,
  ) => void;
  /**
   * Replace a card envelope's props at index `cardIndex`. Used by the
   * ResponseViewProposalCard to flip between `proposal` → `rendered` →
   * `discarded` states without re-emitting the envelope through SSE.
   */
  setCardProps: (
    messageId: string,
    cardIndex: number,
    partialProps: Record<string, unknown>,
  ) => void;
  /**
   * Reset the slice — test-only helper. Per-slice prefixed (MED-2) so the
   * RootStore composition no longer collides on a shared `_reset` name.
   */
  _resetInlineCards: () => void;
}

export type InlineCardsSlice = InlineCardsSliceState & InlineCardsSliceActions;

// ─── Slice creator ──────────────────────────────────────────────────────

function uxKey(messageId: string, envelope: InlineCardEnvelope): string {
  return `${messageId}::${cardIdentityKey(envelope)}`;
}

export const createInlineCardsSlice =
  (): StateCreator<InlineCardsSlice> =>
  (set, get) => ({
    ...INLINE_CARDS_INITIAL_STATE,

    attachCard: (messageId, envelope) => {
      const list = get().cardsByMessageId[messageId] ?? [];
      const targetKey = cardIdentityKey(envelope);
      const existingIndex = list.findIndex(
        (e) => cardIdentityKey(e) === targetKey,
      );
      if (existingIndex >= 0) {
        // Replay-safe no-op. Existing UX state is preserved.
        return existingIndex;
      }
      const next = [...list, envelope];
      set((s) => ({
        ...s,
        cardsByMessageId: { ...s.cardsByMessageId, [messageId]: next },
        cardUxByKey: {
          ...s.cardUxByKey,
          [uxKey(messageId, envelope)]: {},
        },
      }));
      return next.length - 1;
    },

    patchCardUx: (messageId, envelope, patch) => {
      const k = uxKey(messageId, envelope);
      set((s) => ({
        ...s,
        cardUxByKey: {
          ...s.cardUxByKey,
          [k]: { ...(s.cardUxByKey[k] ?? {}), ...patch },
        },
      }));
    },

    setCardProps: (messageId, cardIndex, partialProps) => {
      set((s) => {
        const list = s.cardsByMessageId[messageId];
        if (list === undefined || cardIndex < 0 || cardIndex >= list.length) {
          return s;
        }
        const existing = list[cardIndex];
        if (existing === undefined) return s;
        const updated: InlineCardEnvelope = {
          ...existing,
          props: { ...existing.props, ...partialProps },
        } as InlineCardEnvelope;
        const nextList = [...list];
        nextList[cardIndex] = updated;
        return {
          ...s,
          cardsByMessageId: { ...s.cardsByMessageId, [messageId]: nextList },
        };
      });
    },

    _resetInlineCards: () => {
      set((s) => ({ ...s, ...INLINE_CARDS_INITIAL_STATE }));
    },
  });

/** Vanilla store factory for tests. */
export function createInlineCardsStore() {
  return createStore<InlineCardsSlice>()(createInlineCardsSlice());
}

// ─── Selectors ───────────────────────────────────────────────────────────

export function selectCardsForMessage(messageId: string) {
  return (s: InlineCardsSliceState): InlineCardEnvelope[] =>
    s.cardsByMessageId[messageId] ?? [];
}

export function selectCardUx(messageId: string, envelope: InlineCardEnvelope) {
  return (s: InlineCardsSliceState): InlineCardUxState =>
    s.cardUxByKey[uxKey(messageId, envelope)] ?? {};
}
